import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Check, 
  X, 
  Star, 
  Users, 
  Music, 
  MessageCircle,
  Clock,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  Edit,
  FileText,
  Plus,
  Save,
  UserPlus,
  Upload,
  Image,
  Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingApplications, setPendingApplications] = useState([]);
  const [bands, setBands] = useState([]);
  const [comments, setComments] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editingBand, setEditingBand] = useState(null);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingBlogPost, setEditingBlogPost] = useState(null); // null for new, post_id for edit
  const [showUserEditor, setShowUserEditor] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'listener' });
  const [managingTracks, setManagingTracks] = useState(null); // { band_id, band_name, tracks: [] }
  const [editingTrack, setEditingTrack] = useState(null);
  const [newTrack, setNewTrack] = useState({ title: '', embed_url: '', track_type: 'single' });
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    featured_band_id: '',
    is_published: false,
    image_url: '',
    video_embed_url: ''
  });

  const fetchData = async () => {
    try {
      // Fetch stats
      const statsRes = await fetch(`${API}/admin/stats`, {
        headers: getAuthHeaders(),
        
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      // Fetch pending applications
      const appsRes = await fetch(`${API}/applications/pending`, {
        headers: getAuthHeaders(),
        
      });
      if (appsRes.ok) {
        setPendingApplications(await appsRes.json());
      }
      
      // Fetch all bands (admin endpoint)
      const bandsRes = await fetch(`${API}/admin/bands`, {
        headers: getAuthHeaders(),
        
      });
      if (bandsRes.ok) {
        setBands(await bandsRes.json());
      }
      
      // Fetch all comments
      const commentsRes = await fetch(`${API}/admin/comments`, {
        headers: getAuthHeaders(),
        
      });
      if (commentsRes.ok) {
        setComments(await commentsRes.json());
      }
      
      // Fetch blog posts
      const blogRes = await fetch(`${API}/admin/blog`, {
        headers: getAuthHeaders(),
        
      });
      if (blogRes.ok) {
        setBlogPosts(await blogRes.json());
      }
      
      // Fetch users
      const usersRes = await fetch(`${API}/admin/users`, {
        headers: getAuthHeaders(),
        
      });
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getAuthHeaders]);

  const handleApprove = async (applicationId) => {
    try {
      const res = await fetch(`${API}/applications/${applicationId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setPendingApplications(prev => prev.filter(a => a.application_id !== applicationId));
        setSelectedApplication(null);
        toast.success('Application approved!');
        fetchData(); // Refresh data
      }
    } catch (error) {
      toast.error('Failed to approve application');
    }
  };

  const handleReject = async (applicationId) => {
    try {
      const res = await fetch(`${API}/applications/${applicationId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ reason: rejectionReason }),
        
      });
      
      if (res.ok) {
        setPendingApplications(prev => prev.filter(a => a.application_id !== applicationId));
        setSelectedApplication(null);
        setRejectionReason('');
        toast.success('Application rejected');
      }
    } catch (error) {
      toast.error('Failed to reject application');
    }
  };

  const handleSetFeatured = async (bandId) => {
    try {
      const res = await fetch(`${API}/admin/bands/${bandId}/feature`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setBands(prev => prev.map(b => ({
          ...b,
          is_featured: b.band_id === bandId
        })));
        toast.success('Featured band updated!');
      }
    } catch (error) {
      toast.error('Failed to update featured band');
    }
  };

  const handleUnfeature = async (bandId) => {
    try {
      const res = await fetch(`${API}/admin/bands/${bandId}/unfeature`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setBands(prev => prev.map(b => ({
          ...b,
          is_featured: b.band_id === bandId ? false : b.is_featured
        })));
        toast.success('Band unfeatured');
      }
    } catch (error) {
      toast.error('Failed to unfeature band');
    }
  };

  const handleToggleHomepage = async (bandId) => {
    try {
      const res = await fetch(`${API}/admin/bands/${bandId}/toggle-homepage`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setBands(prev => prev.map(b => ({
          ...b,
          show_on_homepage: b.band_id === bandId ? data.show_on_homepage : b.show_on_homepage
        })));
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to toggle homepage visibility');
    }
  };

  const handleDeleteBand = async (bandId, bandName) => {
    if (!confirm(`Delete "${bandName}"? This cannot be undone.`)) return;
    
    try {
      const res = await fetch(`${API}/bands/${bandId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setBands(prev => prev.filter(b => b.band_id !== bandId));
        toast.success('Band deleted');
      }
    } catch (error) {
      toast.error('Failed to delete band');
    }
  };

  const handleUpdateBand = async () => {
    if (!editingBand) return;
    
    try {
      const res = await fetch(`${API}/bands/${editingBand.band_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(editingBand),
        
      });
      
      if (res.ok) {
        setBands(prev => prev.map(b => 
          b.band_id === editingBand.band_id ? editingBand : b
        ));
        setEditingBand(null);
        toast.success('Band updated');
      }
    } catch (error) {
      toast.error('Failed to update band');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!confirm('Delete this comment?')) return;
    
    try {
      const res = await fetch(`${API}/comments/${commentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setComments(prev => prev.filter(c => c.comment_id !== commentId));
        toast.success('Comment deleted');
      }
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleSaveBlogPost = async () => {
    if (!blogForm.title.trim() || !blogForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      const isEditing = !!editingBlogPost;
      const url = isEditing ? `${API}/admin/blog/${editingBlogPost}` : `${API}/admin/blog`;
      const method = isEditing ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(blogForm),
        
      });
      
      if (res.ok) {
        if (isEditing) {
          // Update existing post in state
          setBlogPosts(prev => prev.map(p => 
            p.post_id === editingBlogPost 
              ? { ...p, ...blogForm }
              : p
          ));
          toast.success('Post updated!');
        } else {
          const post = await res.json();
          setBlogPosts(prev => [post, ...prev]);
          toast.success(blogForm.is_published ? 'Post published!' : 'Draft saved');
        }
        setBlogForm({ title: '', content: '', featured_band_id: '', is_published: false, image_url: '', video_embed_url: '' });
        setEditingBlogPost(null);
        setShowBlogEditor(false);
      }
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const handleEditBlogPost = (post) => {
    setBlogForm({
      title: post.title || '',
      content: post.content || '',
      featured_band_id: post.featured_band_id || '',
      is_published: post.is_published || false,
      image_url: post.image_url || '',
      video_embed_url: post.video_embed_url || ''
    });
    setEditingBlogPost(post.post_id);
    setShowBlogEditor(true);
  };

  const handleDeleteBlogPost = async (postId) => {
    if (!confirm('Delete this post?')) return;
    
    try {
      const res = await fetch(`${API}/admin/blog/${postId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setBlogPosts(prev => prev.filter(p => p.post_id !== postId));
        toast.success('Post deleted');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  // User management functions
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(newUser),
        
      });
      
      if (res.ok) {
        const user = await res.json();
        setUsers(prev => [user, ...prev]);
        setNewUser({ email: '', password: '', name: '', role: 'listener' });
        setShowUserEditor(false);
        toast.success('User created!');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Delete this user? This will also delete their bands, playlists, and comments.')) return;
    
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setUsers(prev => prev.filter(u => u.user_id !== userId));
        toast.success('User deleted');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Track management functions
  const handleOpenTrackManager = async (band) => {
    try {
      const res = await fetch(`${API}/bands/${band.band_id}/tracks`, {
        headers: getAuthHeaders(),
        
      });
      if (res.ok) {
        const tracks = await res.json();
        setManagingTracks({ band_id: band.band_id, band_name: band.name, tracks });
      }
    } catch (error) {
      toast.error('Failed to load tracks');
    }
  };

  const handleAddTrack = async () => {
    if (!newTrack.title.trim() || !newTrack.embed_url.trim()) {
      toast.error('Title and embed URL are required');
      return;
    }
    
    try {
      const res = await fetch(`${API}/bands/${managingTracks.band_id}/tracks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(newTrack),
        
      });
      
      if (res.ok) {
        const track = await res.json();
        setManagingTracks(prev => ({
          ...prev,
          tracks: [...prev.tracks, track]
        }));
        setNewTrack({ title: '', embed_url: '', track_type: 'single' });
        toast.success('Track added!');
      }
    } catch (error) {
      toast.error('Failed to add track');
    }
  };

  const handleUpdateTrack = async () => {
    if (!editingTrack) return;
    
    try {
      const res = await fetch(`${API}/tracks/${editingTrack.track_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: editingTrack.title,
          embed_url: editingTrack.embed_url,
          track_type: editingTrack.track_type
        }),
        
      });
      
      if (res.ok) {
        const updated = await res.json();
        setManagingTracks(prev => ({
          ...prev,
          tracks: prev.tracks.map(t => t.track_id === updated.track_id ? updated : t)
        }));
        setEditingTrack(null);
        toast.success('Track updated!');
      }
    } catch (error) {
      toast.error('Failed to update track');
    }
  };

  const handleDeleteTrack = async (trackId) => {
    if (!confirm('Delete this track?')) return;
    
    try {
      const res = await fetch(`${API}/tracks/${trackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setManagingTracks(prev => ({
          ...prev,
          tracks: prev.tracks.filter(t => t.track_id !== trackId)
        }));
        toast.success('Track deleted');
      }
    } catch (error) {
      toast.error('Failed to delete track');
    }
  };

  // Image upload for blog
  const [uploadingBlogImage, setUploadingBlogImage] = useState(false);

  const handleBlogImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error('Image must be less than 50MB');
      return;
    }

    setUploadingBlogImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Get auth token from localStorage directly as backup
      const authHeaders = getAuthHeaders();
      const storedToken = localStorage.getItem('token');
      const headers = {};
      
      // Use auth headers or fallback to stored token
      if (authHeaders && authHeaders['Authorization']) {
        headers['Authorization'] = authHeaders['Authorization'];
      } else if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      if (!headers['Authorization']) {
        toast.error('Please log in again to upload images');
        setUploadingBlogImage(false);
        return;
      }
      
      const res = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setBlogForm(prev => ({ ...prev, image_url: data.url }));
        toast.success('Image uploaded!');
      } else {
        // Clone the response to read it safely (avoids "body stream already read" error)
        const resClone = res.clone();
        let errorMsg = `Upload failed (${res.status})`;
        try {
          const errorData = await resClone.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          try {
            const errorText = await res.text();
            errorMsg = errorText || errorMsg;
          } catch {
            // Use default error message
          }
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingBlogImage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-accent text-4xl text-foreground">ADMIN DASHBOARD</h1>
          <p className="font-mono text-sm text-muted-foreground mt-1">
            Manage bands, applications, blog, and content
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="border-2 border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Users className="w-4 h-4" />
                <span className="font-mono text-xs uppercase">Users</span>
              </div>
              <p className="font-accent text-3xl text-foreground">{stats.total_users}</p>
            </div>
            <div className="border-2 border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Music className="w-4 h-4" />
                <span className="font-mono text-xs uppercase">Bands</span>
              </div>
              <p className="font-accent text-3xl text-foreground">{stats.total_bands}</p>
            </div>
            <div className="border-2 border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Clock className="w-4 h-4" />
                <span className="font-mono text-xs uppercase">Pending</span>
              </div>
              <p className="font-accent text-3xl text-primary">{stats.pending_applications}</p>
            </div>
            <div className="border-2 border-border bg-card p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <MessageCircle className="w-4 h-4" />
                <span className="font-mono text-xs uppercase">Comments</span>
              </div>
              <p className="font-accent text-3xl text-foreground">{stats.total_comments}</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-card border-2 border-border p-1 flex-wrap h-auto">
            <TabsTrigger 
              value="applications" 
              className="font-mono uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="applications-tab"
            >
              Applications ({pendingApplications.length})
            </TabsTrigger>
            <TabsTrigger 
              value="bands" 
              className="font-mono uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="bands-tab"
            >
              Manage Bands
            </TabsTrigger>
            <TabsTrigger 
              value="blog" 
              className="font-mono uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="blog-tab"
            >
              Blog
            </TabsTrigger>
            <TabsTrigger 
              value="comments" 
              className="font-mono uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="comments-tab"
            >
              Comments
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="font-mono uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="users-tab"
            >
              Users ({users.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Applications */}
          <TabsContent value="applications" className="space-y-4">
            <h2 className="font-sans text-xl font-bold">Pending Applications</h2>
            
            {pendingApplications.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground">No pending applications</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingApplications.map((app) => (
                  <div 
                    key={app.application_id}
                    className="border-2 border-border bg-card p-4"
                    data-testid={`application-${app.application_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-sans text-lg font-bold text-foreground">
                          {app.name}
                        </h3>
                        <p className="font-mono text-xs text-muted-foreground">
                          {app.band_email || app.submitted_by_email} • {new Date(app.created_at).toLocaleDateString()}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {app.genre_tags?.map((tag, i) => (
                            <Badge 
                              key={i}
                              variant="outline"
                              className="font-mono text-[10px] uppercase"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <p className="font-mono text-sm text-muted-foreground mt-2 line-clamp-2">
                          {app.bio}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedApplication(app)}
                          className="font-mono text-xs"
                          data-testid={`view-application-${app.application_id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(app.application_id)}
                          className="bg-primary text-black font-mono text-xs"
                          data-testid={`approve-${app.application_id}`}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Manage Bands */}
          <TabsContent value="bands" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-sans text-xl font-bold">Manage All Bands</h2>
            </div>
            <p className="font-mono text-sm text-muted-foreground">
              Feature bands, hide from homepage, edit details, or delete
            </p>
            
            <div className="space-y-3">
              {bands.map((band) => (
                <div 
                  key={band.band_id}
                  className={`border-2 p-4 ${
                    band.is_featured ? 'border-primary bg-primary/5' : 'border-border bg-card'
                  }`}
                  data-testid={`band-manage-${band.band_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {band.is_featured && (
                        <Star className="w-5 h-5 text-primary fill-current flex-shrink-0 mt-1" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-sans font-bold text-foreground">{band.name}</h3>
                          {!band.show_on_homepage && (
                            <Badge variant="outline" className="text-[10px] text-muted-foreground">
                              Hidden
                            </Badge>
                          )}
                        </div>
                        <p className="font-mono text-xs text-muted-foreground">
                          {band.email || 'No email'} • {band.location || 'No location'}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {band.genre_tags?.slice(0, 3).map((tag, i) => (
                            <Badge 
                              key={i}
                              variant="outline"
                              className="font-mono text-[10px] uppercase"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Link to={`/band/${band.band_id}`}>
                        <Button variant="ghost" size="sm" className="font-mono text-xs h-8 px-2">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </Link>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenTrackManager(band)}
                        className="font-mono text-xs h-8 px-2 text-primary"
                        data-testid={`tracks-band-${band.band_id}`}
                        title="Manage Tracks"
                      >
                        <Music className="w-3 h-3" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingBand(band)}
                        className="font-mono text-xs h-8 px-2"
                        data-testid={`edit-band-${band.band_id}`}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleHomepage(band.band_id)}
                        className="font-mono text-xs h-8 px-2"
                        data-testid={`toggle-homepage-${band.band_id}`}
                      >
                        {band.show_on_homepage ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                      
                      {band.is_featured ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnfeature(band.band_id)}
                          className="font-mono text-xs h-8 px-2 text-primary"
                          data-testid={`unfeature-${band.band_id}`}
                        >
                          <Star className="w-3 h-3 fill-current" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetFeatured(band.band_id)}
                          className="font-mono text-xs h-8 px-2"
                          data-testid={`feature-${band.band_id}`}
                        >
                          <Star className="w-3 h-3" />
                        </Button>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBand(band.band_id, band.name)}
                        className="font-mono text-xs h-8 px-2 text-destructive hover:text-destructive/80"
                        data-testid={`delete-band-${band.band_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Blog */}
          <TabsContent value="blog" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-sans text-xl font-bold">Blog Posts</h2>
              <Button
                onClick={() => {
                  setBlogForm({ title: '', content: '', featured_band_id: '', is_published: false, image_url: '', video_embed_url: '' });
                  setEditingBlogPost(null);
                  setShowBlogEditor(true);
                }}
                className="bg-primary text-black font-mono uppercase"
                data-testid="new-blog-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
            
            {blogPosts.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground">No blog posts yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blogPosts.map((post) => (
                  <div 
                    key={post.post_id}
                    className="border-2 border-border bg-card p-4"
                    data-testid={`blog-post-${post.post_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-sans font-bold text-foreground">{post.title}</h3>
                          <Badge variant={post.is_published ? "default" : "outline"} className="text-[10px]">
                            {post.is_published ? 'Published' : 'Draft'}
                          </Badge>
                        </div>
                        <p className="font-mono text-xs text-muted-foreground mt-1">
                          By {post.author_name} • {new Date(post.created_at).toLocaleDateString()}
                        </p>
                        <p className="font-mono text-sm text-muted-foreground mt-2 line-clamp-2">
                          {post.content}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditBlogPost(post)}
                          className="text-muted-foreground hover:text-primary"
                          data-testid={`edit-blog-${post.post_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteBlogPost(post.post_id)}
                          className="text-destructive hover:text-destructive/80"
                          data-testid={`delete-blog-${post.post_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Comments Moderation */}
          <TabsContent value="comments" className="space-y-4">
            <h2 className="font-sans text-xl font-bold">Comments Moderation</h2>
            
            {comments.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground">No comments to moderate</p>
              </div>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div 
                    key={comment.comment_id}
                    className="border-2 border-border bg-card p-4"
                    data-testid={`comment-${comment.comment_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-mono text-xs text-primary">
                            {comment.user_name}
                          </span>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            on &quot;{comment.track_title}&quot;
                          </span>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            • {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-mono text-sm text-foreground">
                          {comment.body}
                        </p>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteComment(comment.comment_id)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`delete-comment-${comment.comment_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-sans text-xl font-bold">User Management</h2>
              <Button
                onClick={() => setShowUserEditor(true)}
                className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase text-xs"
                data-testid="add-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </div>
            
            {/* Filter tabs for user types */}
            <div className="flex gap-4 border-b border-border pb-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
                  Bands: {users.filter(u => u.role === 'band').length}
                </Badge>
                <Badge variant="outline" className="bg-muted text-muted-foreground">
                  Listeners: {users.filter(u => u.role === 'listener').length}
                </Badge>
                <Badge variant="outline" className="bg-secondary/10 text-secondary border-secondary">
                  Admins: {users.filter(u => u.role === 'admin').length}
                </Badge>
              </div>
            </div>
            
            {users.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground">No users found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Band Users Section */}
                <h3 className="font-mono text-sm uppercase text-primary mt-4">Band Accounts</h3>
                {users.filter(u => u.role === 'band').map((user) => (
                  <div 
                    key={user.user_id}
                    className="border-2 border-primary/30 bg-card p-4"
                    data-testid={`user-${user.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/20 border border-primary flex items-center justify-center">
                          <Music className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-sans font-bold text-foreground">{user.name}</span>
                            <Badge variant="outline" className="text-[10px] font-mono uppercase text-primary border-primary">
                              {user.role}
                            </Badge>
                          </div>
                          <p className="font-mono text-xs text-muted-foreground">{user.email}</p>
                          {user.band_info && (
                            <Link to={`/band/${user.band_info.band_id}`} className="font-mono text-xs text-primary hover:underline">
                              Band: {user.band_info.name}
                            </Link>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(user.user_id)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`delete-user-${user.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Listener Users Section */}
                <h3 className="font-mono text-sm uppercase text-muted-foreground mt-6">Listener Accounts</h3>
                {users.filter(u => u.role === 'listener').map((user) => (
                  <div 
                    key={user.user_id}
                    className="border-2 border-border bg-card p-4"
                    data-testid={`user-${user.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-muted border border-border flex items-center justify-center">
                          <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-sans font-bold text-foreground">{user.name}</span>
                            <Badge variant="outline" className="text-[10px] font-mono uppercase">
                              {user.role}
                            </Badge>
                          </div>
                          <p className="font-mono text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(user.user_id)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`delete-user-${user.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Admin Users Section */}
                <h3 className="font-mono text-sm uppercase text-secondary mt-6">Admin Accounts</h3>
                {users.filter(u => u.role === 'admin').map((user) => (
                  <div 
                    key={user.user_id}
                    className="border-2 border-secondary/30 bg-card p-4"
                    data-testid={`user-${user.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-secondary/20 border border-secondary flex items-center justify-center">
                          <Star className="w-5 h-5 text-secondary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-sans font-bold text-foreground">{user.name}</span>
                            <Badge variant="outline" className="text-[10px] font-mono uppercase text-secondary border-secondary">
                              {user.role}
                            </Badge>
                          </div>
                          <p className="font-mono text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      {/* Don't show delete for admins or show with warning */}
                      <span className="font-mono text-[10px] text-muted-foreground">Protected</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Application Detail Dialog */}
        <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
          <DialogContent className="bg-card border-2 border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-sans text-2xl">
                {selectedApplication?.name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedApplication && (
              <div className="space-y-6 pt-4">
                <div>
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Contact Email</h4>
                  <p className="font-mono text-sm">{selectedApplication.band_email || selectedApplication.submitted_by_email}</p>
                </div>
                
                <div>
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Bio</h4>
                  <p className="font-mono text-sm whitespace-pre-wrap">{selectedApplication.bio}</p>
                </div>
                
                <div>
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Genres</h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedApplication.genre_tags?.map((tag, i) => (
                      <Badge key={i} variant="outline" className="font-mono text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {selectedApplication.location && (
                  <div>
                    <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Location</h4>
                    <p className="font-mono text-sm">{selectedApplication.location}</p>
                  </div>
                )}
                
                {selectedApplication.logo_url && (
                  <div>
                    <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Logo</h4>
                    <img src={selectedApplication.logo_url} alt="Logo" className="w-24 h-24 object-contain border border-border" />
                  </div>
                )}
                
                {selectedApplication.image_urls?.length > 0 && (
                  <div>
                    <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Images</h4>
                    <div className="flex gap-2 flex-wrap">
                      {selectedApplication.image_urls.map((url, i) => (
                        <img key={i} src={url} alt={`Image ${i+1}`} className="w-20 h-20 object-cover border border-border" />
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Tracks</h4>
                  {selectedApplication.embed_tracks?.map((track, i) => (
                    <div key={i} className="border border-border p-2 mb-2">
                      <p className="font-mono text-sm font-bold">{track.title || 'Untitled'}</p>
                      <p className="font-mono text-xs text-muted-foreground truncate">
                        {track.embed_url}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-border pt-4">
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">
                    Rejection Reason (optional)
                  </h4>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    className="bg-input border border-border font-mono text-sm"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleApprove(selectedApplication.application_id)}
                    className="flex-1 bg-primary text-black font-mono uppercase"
                    data-testid="dialog-approve-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleReject(selectedApplication.application_id)}
                    className="flex-1 font-mono uppercase"
                    data-testid="dialog-reject-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Band Dialog */}
        <Dialog open={!!editingBand} onOpenChange={() => setEditingBand(null)}>
          <DialogContent className="bg-card border-2 border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-sans text-2xl">
                Edit Band: {editingBand?.name}
              </DialogTitle>
            </DialogHeader>
            
            {editingBand && (
              <div className="space-y-4 pt-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Name</Label>
                    <Input
                      value={editingBand.name}
                      onChange={(e) => setEditingBand(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-input border border-border font-mono"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Email</Label>
                    <Input
                      value={editingBand.email || ''}
                      onChange={(e) => setEditingBand(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-input border border-border font-mono"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Bio</Label>
                  <Textarea
                    value={editingBand.bio}
                    onChange={(e) => setEditingBand(prev => ({ ...prev, bio: e.target.value }))}
                    className="bg-input border border-border font-mono min-h-[100px]"
                  />
                </div>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Location</Label>
                    <Input
                      value={editingBand.location || ''}
                      onChange={(e) => setEditingBand(prev => ({ ...prev, location: e.target.value }))}
                      className="bg-input border border-border font-mono"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase">Website</Label>
                    <Input
                      value={editingBand.website || ''}
                      onChange={(e) => setEditingBand(prev => ({ ...prev, website: e.target.value }))}
                      className="bg-input border border-border font-mono"
                    />
                  </div>
                </div>
                
                {/* Logo URL */}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Logo URL</Label>
                  <div className="flex gap-2 items-start">
                    <Input
                      value={editingBand.logo_url || ''}
                      onChange={(e) => setEditingBand(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://..."
                      className="bg-input border border-border font-mono flex-1"
                    />
                    {editingBand.logo_url && (
                      <img 
                        src={editingBand.logo_url} 
                        alt="Logo" 
                        className="w-12 h-12 object-contain border border-border"
                      />
                    )}
                  </div>
                </div>
                
                {/* Image URLs */}
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase">Image URLs (comma-separated)</Label>
                  <Textarea
                    value={(editingBand.image_urls || []).join('\n')}
                    onChange={(e) => setEditingBand(prev => ({ 
                      ...prev, 
                      image_urls: e.target.value.split('\n').map(url => url.trim()).filter(url => url) 
                    }))}
                    placeholder="Enter image URLs, one per line..."
                    className="bg-input border border-border font-mono min-h-[80px]"
                  />
                  {editingBand.image_urls?.length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {editingBand.image_urls.filter(url => url).map((url, i) => (
                        <img 
                          key={i} 
                          src={url} 
                          alt={`Image ${i+1}`} 
                          className="w-16 h-16 object-cover border border-border"
                        />
                      ))}
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={handleUpdateBand}
                  className="w-full bg-primary text-black font-mono uppercase"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Track Manager Dialog */}
        <Dialog open={!!managingTracks} onOpenChange={() => { setManagingTracks(null); setEditingTrack(null); }}>
          <DialogContent className="bg-card border-2 border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-sans text-2xl flex items-center gap-2">
                <Music className="w-5 h-5 text-primary" />
                Tracks: {managingTracks?.band_name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {/* Add New Track Form */}
              <div className="border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
                <h3 className="font-mono text-xs uppercase text-primary">Add New Track</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    value={newTrack.title}
                    onChange={(e) => setNewTrack(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Track Title"
                    className="bg-input border border-border font-mono"
                  />
                  <select
                    value={newTrack.track_type}
                    onChange={(e) => setNewTrack(prev => ({ ...prev, track_type: e.target.value }))}
                    className="bg-input border border-border font-mono text-sm p-2"
                  >
                    <option value="single">Single</option>
                    <option value="album">Album</option>
                    <option value="ep">EP</option>
                  </select>
                </div>
                <Input
                  value={newTrack.embed_url}
                  onChange={(e) => setNewTrack(prev => ({ ...prev, embed_url: e.target.value }))}
                  placeholder="Embed URL (Spotify, YouTube, SoundCloud, Bandcamp)"
                  className="bg-input border border-border font-mono"
                />
                <Button
                  onClick={handleAddTrack}
                  className="w-full bg-primary text-black font-mono uppercase text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Track
                </Button>
              </div>
              
              {/* Existing Tracks */}
              <div className="space-y-3">
                <h3 className="font-mono text-xs uppercase text-muted-foreground">
                  Existing Tracks ({managingTracks?.tracks?.length || 0})
                </h3>
                
                {managingTracks?.tracks?.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-border">
                    <Music className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="font-mono text-sm text-muted-foreground">No tracks yet</p>
                  </div>
                ) : (
                  managingTracks?.tracks?.map((track) => (
                    <div 
                      key={track.track_id}
                      className="border-2 border-border bg-card p-4"
                    >
                      {editingTrack?.track_id === track.track_id ? (
                        <div className="space-y-3">
                          <div className="grid gap-3 sm:grid-cols-2">
                            <Input
                              value={editingTrack.title}
                              onChange={(e) => setEditingTrack(prev => ({ ...prev, title: e.target.value }))}
                              className="bg-input border border-border font-mono"
                            />
                            <select
                              value={editingTrack.track_type}
                              onChange={(e) => setEditingTrack(prev => ({ ...prev, track_type: e.target.value }))}
                              className="bg-input border border-border font-mono text-sm p-2"
                            >
                              <option value="single">Single</option>
                              <option value="album">Album</option>
                              <option value="ep">EP</option>
                            </select>
                          </div>
                          <Input
                            value={editingTrack.embed_url}
                            onChange={(e) => setEditingTrack(prev => ({ ...prev, embed_url: e.target.value }))}
                            className="bg-input border border-border font-mono text-sm"
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={handleUpdateTrack}
                              size="sm"
                              className="bg-primary text-black font-mono text-xs"
                            >
                              <Save className="w-3 h-3 mr-1" />
                              Save
                            </Button>
                            <Button
                              onClick={() => setEditingTrack(null)}
                              variant="ghost"
                              size="sm"
                              className="font-mono text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-sans font-bold text-foreground">{track.title}</span>
                              <Badge variant="outline" className="text-[10px] font-mono uppercase">
                                {track.track_type}
                              </Badge>
                            </div>
                            <p className="font-mono text-xs text-muted-foreground truncate max-w-md">
                              {track.embed_url}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingTrack(track)}
                              className="h-8 w-8"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteTrack(track.track_id)}
                              className="h-8 w-8 text-destructive hover:text-destructive/80"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Blog Editor Dialog */}
        <Dialog open={showBlogEditor} onOpenChange={(open) => {
          setShowBlogEditor(open);
          if (!open) {
            setEditingBlogPost(null);
            setBlogForm({ title: '', content: '', featured_band_id: '', is_published: false, image_url: '', video_embed_url: '' });
          }
        }}>
          <DialogContent className="bg-card border-2 border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-sans text-2xl">
                {editingBlogPost ? 'Edit Blog Post' : 'New Blog Post'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Title *</Label>
                <Input
                  value={blogForm.title}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Post title"
                  className="bg-input border border-border font-mono"
                  data-testid="blog-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Content *</Label>
                <Textarea
                  value={blogForm.content}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your post..."
                  className="bg-input border border-border font-mono min-h-[200px]"
                  data-testid="blog-content-input"
                />
              </div>
              
              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Featured Image (optional)
                </Label>
                {blogForm.image_url ? (
                  <div className="flex items-center gap-4">
                    <img src={blogForm.image_url} alt="Blog preview" className="w-32 h-20 object-cover border border-border" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setBlogForm(prev => ({ ...prev, image_url: '' }))}
                      className="text-destructive"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-4 items-center">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-border hover:border-primary px-4 py-3 flex items-center gap-2 transition-colors">
                        <Upload className="w-5 h-5 text-muted-foreground" />
                        <span className="font-mono text-sm text-muted-foreground">
                          {uploadingBlogImage ? 'Uploading...' : 'Upload Image'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleBlogImageUpload}
                        className="hidden"
                        disabled={uploadingBlogImage}
                      />
                    </label>
                    <span className="font-mono text-xs text-muted-foreground">or</span>
                    <Input
                      value={blogForm.image_url}
                      onChange={(e) => setBlogForm(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="Paste image URL..."
                      className="bg-input border border-border font-mono flex-1"
                    />
                  </div>
                )}
              </div>
              
              {/* Video Embed */}
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Video Embed (optional)
                </Label>
                <Input
                  value={blogForm.video_embed_url}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, video_embed_url: e.target.value }))}
                  placeholder="YouTube or Vimeo URL"
                  className="bg-input border border-border font-mono"
                />
                <p className="font-mono text-[10px] text-muted-foreground">
                  Paste a YouTube or Vimeo link. Regular links like youtube.com/watch?v=... will be auto-converted.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Feature a Band (optional)</Label>
                <select
                  value={blogForm.featured_band_id}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, featured_band_id: e.target.value }))}
                  className="w-full bg-input border border-border font-mono text-sm p-2"
                  data-testid="blog-band-select"
                >
                  <option value="">No featured band</option>
                  {bands.map(band => (
                    <option key={band.band_id} value={band.band_id}>{band.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="publish"
                  checked={blogForm.is_published}
                  onChange={(e) => setBlogForm(prev => ({ ...prev, is_published: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Label htmlFor="publish" className="font-mono text-sm">Publish immediately</Label>
              </div>
              
              <Button
                onClick={handleSaveBlogPost}
                className="w-full bg-primary text-black font-mono uppercase"
                data-testid="save-blog-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingBlogPost 
                  ? 'Update Post' 
                  : (blogForm.is_published ? 'Publish' : 'Save Draft')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={showUserEditor} onOpenChange={setShowUserEditor}>
          <DialogContent className="bg-card border-2 border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-sans">Create New User</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Name *</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  className="bg-input border border-border font-mono"
                  data-testid="new-user-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="bg-input border border-border font-mono"
                  data-testid="new-user-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Password *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Password"
                  className="bg-input border border-border font-mono"
                  data-testid="new-user-password"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase">Role</Label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full bg-input border border-border font-mono text-sm p-2"
                  data-testid="new-user-role"
                >
                  <option value="listener">Listener</option>
                  <option value="band">Band</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <Button
                onClick={handleCreateUser}
                className="w-full bg-primary text-black font-mono uppercase"
                data-testid="create-user-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
